import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import colors from 'utils/colors'
import { icons } from 'utils/icons'
import { IRoom } from 'types/rooms.types'
import { IGroup } from 'types/group.types'
import { NAVIGATION } from 'locales/locales'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useLazyUnviewQuery,
  useViewMutation,
  useLazyGetRoomsQuery,
} from 'app/services/room.service'
import { useLazyGetChannelsQuery } from 'app/services/group.service'
import { roomsActions } from 'app/slices/rooms.slice'
import { groupsActions } from 'app/slices/groups.slice'
import {
  getNotificationBadgeForMessages,
  getNotificationBadgeForTeams,
} from 'utils/helpers/notification'

import Options from './options/Options'
import CustomIcon from 'components/icon/CustomIcon'
import CustomText from 'components/text/CustomText'
import Avatar, { AvatarSize } from 'components/avatar/Avatar'
import AccountSettings from 'features/account-settings/AccountSettings'

import useWindowFocus from 'utils/hooks/useWindowFocus'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import {
  IRolePermissions,
  MessagingPermissions,
  NewsFeedPermissions,
  TeamAndChannelPermissions,
  VideoConferencePermissions,
} from 'types/role.types'
import { FixMeLater } from 'types'
import { useLogoUrl } from 'utils/hooks/useLogoUrl'
// import { FILE_HOST } from 'utils/helpers/apiHelper'

import useUnviewAllOnLeave from 'utils/hooks/useUnviewAllOnLeave'

interface UStyledProps {
  marginBottom?: number
  isActive?: boolean
  hasNotification?: boolean
}

const regex = /[0-9A-Fa-f]{6}/g

const NavigationBar = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [updateView] = useViewMutation()
  const [unViewMessages] = useLazyUnviewQuery()
  const [getRooms] = useLazyGetRoomsQuery()
  const [getChannels] = useLazyGetChannelsQuery()
  const isFocus = useWindowFocus()
  // const [closeThread] = useCloseThreadMutation()

  useEffect(() => {
    if (location.pathname === '/') {
      navigate('news-feed')
    }
  }, [])

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)

  const hasNewsFeedViewPermission = isFlagSetIn(
    [NewsFeedPermissions.VIEW_NEWSFEED],
    permissions.newsFeed,
  )
  const hasVideoCallPermission = isFlagSetIn(
    [VideoConferencePermissions.VIEW_ALL_MEETING_LIST],
    permissions.videoConference,
  )
  const hasMessageViewPermission = isFlagSetIn(
    [MessagingPermissions.START_MESSAGING],
    permissions.messaging,
  )
  const hasTeamsViewPermission = isFlagSetIn(
    [TeamAndChannelPermissions.VIEW_TEAMS],
    permissions.teamAndChannel,
  )

  const authUser = useAppSelector((state) => state.auth.user)
  const rooms = useAppSelector((state) => state.rooms.data)
  const groups = useAppSelector((state) => state.groups.data)
  const users = useAppSelector((state) => state.users.data)
  // const currentlyOpenThread = useAppSelector((state) => state.message.currentlyOpenThread)

  const { unreadNotificationCount, realNotificationCount } = useAppSelector(
    (state) => state.newsFeed,
  )

  const systemSettings = useAppSelector((state) => state.system.data)
  const { logoUrl, logoRef } = useLogoUrl(systemSettings?.imageUrlLogo)

  const { roomId, channelId } = useParams()

  const [visible, setVisible] = useState(false)
  const [visibleAccountSettings, setVisibleAccountSettings] = useState(false)

  const notificationBadgeForMessages = getNotificationBadgeForMessages(
    rooms.filter((room: any) => room?.isAppear === true && room.type !== 'CM'),
  )
  const notificationBadgeForTeams = getNotificationBadgeForTeams(groups)

  const w = globalThis as FixMeLater

  // useEffect(() => {
  //   const w = window as any
  //   if (w.api) {
  //     if (notificationBadgeForMessages)
  //       w.api.send('sendNotify', {
  //         type: 'messages',
  //         title: t(INFORMATION.NEW_NOTIFICATION),
  //         msg: t(INFORMATION.MESSAGES_RECEIVED),
  //       })

  //     if (notificationBadgeForTeams)
  //       w.api.send('sendNotify', {
  //         type: 'teams',
  //         title: t(INFORMATION.NEW_NOTIFICATION),
  //         msg: t(INFORMATION.CHANNEL_MESSAGES_RECEIVED),
  //       })

  //     if (unreadNotificationCount > 0 || realNotificationCount > 0)
  //       w.api.send('sendNotify', {
  //         type: 'newsfeed',
  //         title: t(INFORMATION.NEW_NOTIFICATION),
  //         msg: t(INFORMATION.NEWSFEED_RECEIVED),
  //       })
  //   }
  // }, [
  //   unreadNotificationCount,
  //   realNotificationCount,
  //   notificationBadgeForTeams,
  //   notificationBadgeForMessages,
  // ])

  useEffect(() => {
    const status = users[authUser._id]?.status
    if (w.api && status) {
      w.api.send('sendStatus', { status })
    }
  }, [users[authUser?._id]?.status, w])

  const relavantPages = !!(roomId?.match(regex) || channelId?.match(regex))

  useEffect(() => {
    async function _changeRoomId() {
      if (!relavantPages || !isFocus || !(roomId || channelId)) return
      const currentId = roomId || channelId
      try {
        await updateView({ roomId: currentId }).unwrap()
        // State'i hemen güncelle: açık olan oda/kanal unread 0
        if (channelId) {
          dispatch(groupsActions.updateUnreadCount({ roomId: channelId, increase: false }))
        } else if (roomId) {
          dispatch(roomsActions.updateUnreadCount({ roomId, increase: false }))
        }
        // getRooms/getChannels refetch ile tüm liste API'daki güncel unreadCount ile senkronize olsun
        await Promise.all([getRooms(undefined), getChannels(undefined)])
      } catch {
        // updateView hata verirse sessizce devam et
      }
    }
    _changeRoomId()
  }, [roomId, channelId, isFocus, relavantPages, dispatch, updateView, getRooms, getChannels])

  // useEffect(() => {
  //   if (!relavantPages && isFocus) {
  //     unViewMessages({ roomId: 'unviewAll' })
  //   }
  // }, [isFocus, relavantPages])

  useUnviewAllOnLeave({
    relavantPages: !!relavantPages,
    onUnviewAll: async () => {

      await unViewMessages({
        roomId: 'unviewAll',
        t: Date.now(),
      }).unwrap()

    },
  })

  const firstAvailableRoom = rooms.find(
    (room: IRoom) => room?.isAppear === true && ['DM', 'GM'].includes(room?.type),
  )
  // const threads = useAppSelector((state) => state.message.threadList)
  // const firstAvailableThread = threads[0]

  const AvailableChannels = groups.map((group: IGroup) =>
    group?.channels?.find((channel) => channel.isAppear === true),
  )
  const firstAvailableChannel = AvailableChannels?.[0]?._id

  const navigateByRouteName = (routeName: string) => {

    if (!routeName) return

    const isMessagesRoute = routeName.includes('/messages')
   // const isThreadsRoute = routeName.includes('/threads')
    const isChannelsRoute = routeName.includes('/channels')

    // If these routes need an id and we don't have one, avoid navigating to a broken page.
    if (
      (isMessagesRoute|| isChannelsRoute) &&
      routeName.endsWith('/')
    ) {
      return
    }

    navigate(routeName)
  }

  const onToggleVisible = () => {
    setVisible(!visible)
  }

  const onCloseAccountSettings = () => {
    setVisibleAccountSettings(false)
  }

  const onOpenAccountSettings = () => {
    setVisibleAccountSettings(true)
    onToggleVisible()
  }

  const channelRedirect =
    firstAvailableChannel !== undefined && hasTeamsViewPermission
      ? `/channels/${firstAvailableChannel}`
      : '/channels'

  const messagesRedirect =
    (firstAvailableRoom !== undefined && hasMessageViewPermission ? '/messages/' + firstAvailableRoom?._id : '/messages')
  // const threadsRedirect =
  //   firstAvailableThread?._id && hasMessageViewPermission
  //     ? `/threads/${firstAvailableThread._id}`
  //     : '/threads'

  return (
    <Wrapper>
      {visible && (
        <Options onOpenAccountSettings={onOpenAccountSettings} onHide={onToggleVisible} />
      )}
      {visibleAccountSettings && <AccountSettings onClose={onCloseAccountSettings} />}

      <Avatar
        onClick={onToggleVisible}
        type="circle"
        size={AvatarSize.large}
        source={authUser?.avatar}
        text={authUser?.fullname}
        status={users[authUser._id]?.status}
        color={colors.orange}
      />

      <ScrollableDiv>
        <Dock>
          {hasNewsFeedViewPermission && (
            <Section
              isActive={location.pathname === '/news-feed'}
              onClick={() => {
                navigateByRouteName('/news-feed')
              }}
            >
              <CustomIcon source={icons.navbarNewsfeedIcon} />
              <StyledCustomText typography="header5" color={colors.white}>
                {t(NAVIGATION.NEWS_FEED)}
              </StyledCustomText>
              {unreadNotificationCount > 0 || realNotificationCount > 0 ? (
                <RedDot hasNotification={unreadNotificationCount > 0} />
              ) : (
                ''
              )}
            </Section>
          )}
          {
            <Section
              isActive={location.pathname.includes('channels')}
              onClick={() => navigateByRouteName(channelRedirect)}
            >
              <CustomIcon source={icons.navbarTeamsIcon} />
              <StyledCustomText typography="header5" color={colors.white}>
                {t(NAVIGATION.TEAMS)}
              </StyledCustomText>
              {notificationBadgeForTeams && hasTeamsViewPermission && (
                <RedDot hasNotification={notificationBadgeForTeams} />
              )}
            </Section>
          }

          {hasMessageViewPermission && (
            <Section
              isActive={location.pathname.includes('messages')}
              onClick={() => navigateByRouteName(messagesRedirect)}
            >
              <CustomIcon source={icons.navbarMessagesIcon} />
              <StyledCustomText typography="header5" color={colors.white}>
                {t(NAVIGATION.MESSAGES)}
              </StyledCustomText>
              {notificationBadgeForMessages && (
                <RedDot hasNotification={notificationBadgeForMessages} />
              )}
            </Section>
          )}

          {hasVideoCallPermission && (
            <Section
              isActive={location.pathname.includes('video-call')}
              onClick={() => navigateByRouteName('/video-call')}
            >
              <CustomIcon source={icons.navbarVideoIcon} />

              <StyledCustomText typography="header5" color={colors.white}>
                {t(NAVIGATION.VIDEO_CALL)}
              </StyledCustomText>
            </Section>
          )}
          {/* <Section
            isActive={location.pathname.includes('live-stream')}
            onClick={() => navigateByRouteName('/live-stream')}
          >
            <CustomIcon source={icons.liveStreamIcon} iconPreset="xLarge" />
            <StyledCustomText typography="header5" color={colors.white}>
              {t(NAVIGATION.LIVE_STREAM)}
            </StyledCustomText>
          </Section> */}
        </Dock>
      </ScrollableDiv>
      <Logo ref={logoRef} src={logoUrl} />
    </Wrapper>
  )
}

export default NavigationBar

export const Wrapper = styled.div`
  width: 80px;
  height: 100%;
  padding: 10px 0px;
  background-color: ${colors.vimeDark};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 6px 6px 20px rgba(0, 0, 0, 0.1);
`

export const ScrollableDiv = styled.div`
  overflow-y: hidden;
  height: 100%;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none;
  }
  li img {
    transition: all 0.3s;
    transform-origin: 50% 100%;
  }
  li:hover img {
    transform: scale(1.05);
    margin: 0 2em;
  }

  li:hover div {
    transform: scale(1.05);
  }
  li {
    overflow: hidden;
    margin: 0 auto;
    padding: 10px 0;
  }
  margin-top: 40px;
`
export const Section = styled.li.attrs((props: UStyledProps) => props)`
  @keyframes blinker {
    50% {
      opacity: 0;
    }
  }
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${({ marginBottom }) => marginBottom || 20}px;
  row-gap: 4px;
  opacity: ${({ isActive = false }) => (isActive ? '1' : '.8')};

  div {
    transition: transform 0.3s;
    text-align: center;
    color: ${colors.white};
  }
  img {
    transform: ${({ isActive = false }) => (isActive ? 'scale(1.0)' : 'scale(0.8)')};
  }
`

export const Logo = styled.img`
  margin-top: 10px;
  padding: 5px;
  width: 80px;
  max-height: 50px;
  height: auto;
  object-fit: contain;
`

export const TriangleIcon = styled(CustomIcon)`
  position: absolute;
  left: 71px;
  top: 6px;
`
export const RedDot = styled.span`
  animation: ${({ hasNotification }: { hasNotification?: boolean }) =>
    hasNotification ? 'blinker 1.5s linear infinite;' : 'none'};
  position: absolute;
  width: 10px;
  height: 10px;
  z-index: 2;
  background-color: ${colors.red};
  border-radius: 50%;
  right: 8px;
`

export const Dock = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 10px;
  overflow-y: hidden;
`

export const StyledCustomText = styled(CustomText)`
  width: 100%;
  text-align: -webkit-center !important;
  padding: 2px 4px;
  border-radius: 4px;
`
